import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { getSearchUpsellTracking } from './searchUpsellTracking';
import { getSearchUpsellTracking as getExperimentTracking } from './searchUpsellExperiment';

type Props = {
  query: string;
  url: string;
  trackExperiment?: boolean;
};

const Container = styled(ImpressionWatcher).attrs({
  as: 'section',
  shouldTrackClick: (event: React.MouseEvent<HTMLElement>) =>
    event.target instanceof Element && event.target.closest('a') !== null,
})`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  margin-bottom: var(--spacing-sm-32, 32px);
  padding: var(--spacing-sm-24, 24px);
  border: 1px solid var(--background-beige-dark, #e2e0d9);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--background-beige-light, #f9f8f6);

  @include breakpoint(medium, large) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Copy = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
`;

const Title = styled(Heading).attrs({ as: 'h2', size: 'sm' })`
  margin: 0;
`;

const Description = styled(Body).attrs({ size: 'sm' })`
  max-width: 720px;
  margin: 0;
  color: var(--text-beige-secondary, #585448);
`;

const PaperFinderCopy: React.FC<Pick<Props, 'query'>> = ({ query }) => (
  <Copy>
    <Title>Closed access papers do not have to stay closed</Title>
    <Description>
      Some recently published papers about &apos;{query}&apos; are closed
      access. Pick the ones you&apos;re interested in and we will try to get you
      the full text.
    </Description>
  </Copy>
);

export const PaperFinderUpsell: React.FC<Props> = ({
  query,
  url,
  trackExperiment = false,
}) => (
  <Container
    {...getSearchUpsellTracking(
      'paper_finder',
      'search_inline',
      'paper-finder-search-upsell',
    )}
    additionalTracking={
      trackExperiment
        ? {
            ...getExperimentTracking(
              'paper_finder',
              'search_inline',
              'paper-finder-search-upsell',
            ),
            noBatch: true,
          }
        : undefined
    }
  >
    <PaperFinderCopy query={query} />
    <ButtonLink href={url}>Find papers</ButtonLink>
  </Container>
);

export const PaperFinderDashboardLink: React.FC<Props> = ({ query, url }) => (
  <Container source="paper_finder_dashboard_search_card" noBatch>
    <PaperFinderCopy query={query} />
    <ButtonLink href={url}>Open Paper Finder</ButtonLink>
  </Container>
);

const preflightUrlFor = (url: string): string => {
  const preflightUrl = new URL(url, window.location.origin);
  preflightUrl.pathname = `${preflightUrl.pathname}.json`;
  preflightUrl.searchParams.set('preflight', 'true');
  return `${preflightUrl.pathname}${preflightUrl.search}`;
};

export const usePaperFinderAvailability = (
  url: string | undefined,
  enabled = true,
): boolean | undefined => {
  const [available, setAvailable] = useState<boolean>();

  useEffect(() => {
    setAvailable(undefined);
    if (!url || !enabled) return;

    let cancelled = false;

    fetch(preflightUrlFor(url))
      .then(async (response) => {
        if (!response.ok) return false;
        return (await response.json()) as { results_available?: boolean };
      })
      .then((result) => {
        if (cancelled) return;

        setAvailable(
          typeof result === 'boolean'
            ? result
            : result.results_available === true,
        );
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, url]);

  return available;
};
